import moment from 'moment';
import pick from 'lodash/pick';
import { Inject, Injectable, Injector, Optional } from '@angular/core';
import {
  ApiService,
  cryptoHelper,
  IBakery,
  IOrderDelivery,
  PutInQueueService,
  WebStorageService
} from '@cakeiteasy/web-utils';
import { Store } from '@ngrx/store';
import { map } from 'rxjs/operators';
import { Observable, Subject, firstValueFrom } from 'rxjs';

import { ICart, IAdditionalDeliveryInfo, IAppliedDiscount, IDispatchCartOptions } from '../models/cart/i-cart';
import { ICakeCartLine } from '../models/cart/i-cake-cart-line';
import { IDeliveryToDoor, IMailDeliveryToDoor, IPickupDelivery } from '../models/delivery';
import {
  ICartDailyStockSoldOutProductType,
  ICartDeliveryInfo,
} from '../models/delivery/i-cart-delivery-info';
import {
  CART_ANALYTIC_TRACKERS,
  CART_DELIVERY_TYPES,
  CART_KEY_STORAGE_KEY,
  CART_LINE_TYPES,
  DISCOUNT_TYPES,
  UI_DELIVERY_OPTIONS
} from '../constants/common';
import { extendWithPrice, sumPrices } from '../helpers/price-data-helper';
import { cartHelper } from '../helpers/cart-helper';
import {
  ApplyDeliveryDeposit,
  ApplyInvoiceFee,
  ApplyPromo,
  CancelInvoiceFee,
  CancelPromo,
  DeliveryInfoUpdate,
  LoadCartSuccess,
  RemoveLine,
  ResetCart,
  SetBakery,
  SetDelivery,
  SetDeliveryActiveStatus,
  UpdateLine
} from '../store/cart.actions';
import { CartState } from '../store/reducers/cart.reducer';
import { ICartLine } from '../models/cart/i-cart-line';
import { CartAnalyticTracker } from '../models/cart/i-cart-analytic-tracker';
import { IReturnPackagingDetails } from '../../return-packaging/models/i-return-packaging-details';
import {
  getReturnSettings,
  isReturnLineOutdated,
  isReturnLineOrphaned,
} from '../../return-packaging/policy/return-packaging.policy';

export enum CART_QUEUE_EVENTS {
  FETCH_CART = 'FETCH_CART',
  SAVE_PRODUCT = 'SAVE_PRODUCT',
  SAVE_PRODUCTS = 'SAVE_PRODUCTS',
  RESET_CART = 'RESET_CART',
  REMOVE_PRODUCT = 'REMOVE_PRODUCT',
  REMOVE_PRODUCT_TYPE = 'REMOVE_PRODUCT_TYPE',
  REMOVE_CATERING_MENU = 'REMOVE_CATERING_MENU',
  SAVE_DELIVERY = 'SAVE_DELIVERY',
  DELETE_DELIVERY_ORDER = 'DELETE_DELIVERY_ORDER',
  GET_CART_DELIVERY_INFO = 'GET_CART_DELIVERY_INFO',
  SAVE_RETURN_PACKAGING = 'SAVE_RETURN_PACKAGING',
}

@Injectable()
export class CartServiceV2 extends PutInQueueService {
  public cart: ICart;
  public cartLinesRemovalSynced$ = new Subject<void>();

  private _actualInvoiceFee: number;
  private trackers: CartAnalyticTracker[];

  constructor(private _api: ApiService,
              private _storage: WebStorageService,
              private _store: Store<CartState>,
              private injector: Injector) {
    super(false);

    this.trackers = this.injector.get(CART_ANALYTIC_TRACKERS, []);
  }

  public static isDeliveryFinished(cart: ICart): boolean {
    if (!cart.delivery) {
      return false;
    }

    if (cart.delivery.type === UI_DELIVERY_OPTIONS.MAIL) {
      const { postNumber, address, orderId } = (cart.delivery as IMailDeliveryToDoor);
      return Boolean(orderId) && Boolean(postNumber) && Boolean(address);
    }

    const { orderId, timeRange, date, type } = cart.delivery as (IDeliveryToDoor | IPickupDelivery);

    const basicCheck = Boolean(orderId) && Boolean(timeRange) && Boolean(date);

    if (type === UI_DELIVERY_OPTIONS.PICKUP) {
      return basicCheck && Boolean((cart.delivery as IPickupDelivery).outletId);
    }

    if (type === UI_DELIVERY_OPTIONS.DELIVERY) {
      const { postNumber, deliveryAgentId } = (cart.delivery as IDeliveryToDoor);
      return basicCheck && Boolean(postNumber) && Boolean(deliveryAgentId);
    }

    throw new Error(`delivery.type === ${type} is not supported`);
  }

  public fetchCart(waitForPendingActions = false,
                   options?: {
                     priceReducePercent?: number,
                     deliveryPrice?: number,
                     customDeliveryPriceEnabled?: boolean
                   }): Promise<ICart> {
    const priceReducePercent = options && options.priceReducePercent || 0;
    const initOptions = { ...options, priceReducePercent, dispatchCart: true };

    const req$ = this._api
      .get('api.cart', 'cart', {})
      .pipe(map((multiOrder) => {
        this.cart = this.initFromCartPayload(multiOrder, initOptions);
        return this.cart;
      }));

    if (waitForPendingActions) {
      return this.putInQueue({
        queueId: CART_QUEUE_EVENTS.FETCH_CART,
        payload: [null]
      }, () => firstValueFrom(req$));
    }

    return firstValueFrom(req$);
  }

  /**Deprecated*/
  public applyDiscount(code: string, countryCode: string) {
    return firstValueFrom(this._api.put('api.cart',
      'promo', null, { code, country: countryCode }))
      .then((multiOrder) => {
        this.cart = this.initFromCartPayload(multiOrder, { dispatchCart: true });
        return this.cart;
      });
  }

  /**Should be renamed on applyDiscount and be used on MP and bakery-shops*/
  public applyBakeryDiscount(code: string) {
    return firstValueFrom(this._api.put('api.cart', 'discount_apply', null, { code }))
      .then((multiOrder) => {
        this.cart = this.initFromCartPayload(multiOrder, { dispatchCart: true });
        return this.cart;
      });
  }

  async removeDiscount() {
    const multiOrder = await firstValueFrom(this._api
      .remove('api.cart', 'discount_remove'));

    this.cart = this.initFromCartPayload(multiOrder, { dispatchCart: true });
    return this.cart;
  }

  public async applyCompanyDiscount(params: { companyId?: number }) {
    const multiOrder = await firstValueFrom(this._api.put('api.cart',
      'apply_company_discount', null, params));

    this.cart = this.initFromCartPayload(multiOrder, { dispatchCart: true });
    return this.cart;
  }

  public async applyCompany(companyId: number = null, dispatchCart = true) {
    const multiOrder = await firstValueFrom(this._api
      .put('api.cart', 'apply_company', null, { company: companyId }));

    this.cart = this.initFromCartPayload(multiOrder, { dispatchCart });
    return this.cart;
  }

  public cancelPromoCode() {
    this._store.dispatch(new CancelPromo());
  }

  public applyManualPromoCode(promo: IAppliedDiscount) {
    this._store.dispatch(new ApplyPromo({ ...promo }));
  }

  public applyManualInvoiceFee(invoiceFee: number) {
    this._actualInvoiceFee = invoiceFee || 0;
    this._store.dispatch(new ApplyInvoiceFee(this._actualInvoiceFee));
  }

  public cancelInvoiceFee() {
    this._actualInvoiceFee = null;
    this._store.dispatch(new CancelInvoiceFee());
  }

  public applyDeliveryDeposit() {
    this._store.dispatch(new ApplyDeliveryDeposit());
  }

  public setDeliveryActiveStatus(status: boolean) {
    this._store.dispatch(new SetDeliveryActiveStatus(status));
  }

  public saveBakeryToStoreIfNecessary(bakery: IBakery) {
    if (!this.cart || !this.cart.bakery) {
      this._store.dispatch(new SetBakery(bakery));
    }
  }

  public saveDeliveryInfoToStoreIfNecessary(data: any) {
    if (!this.cart || !this.cart.delivery) {
      this._store.dispatch(new SetDelivery(data));
    }
  }

  public saveProduct(cartLine: ICakeCartLine, options?: IDispatchCartOptions): Promise<ICart> {
    cartLine = this.normalizeCartLineBeforeSaving(cartLine);
    this.putCartLineToStore(cartLine);
    return this.putInQueueProductSaving(cartLine, options);
  }

  public saveProducts(cartLines: ICakeCartLine[]): Promise<ICart> {
    cartLines = cartLines.map((line) => this.normalizeCartLineBeforeSaving(line));
    cartLines.forEach((cartLine) => this.putCartLineToStore(cartLine));

    return this.putInQueue({
      queueId: CART_QUEUE_EVENTS.SAVE_PRODUCTS,
      payload: [cartLines]
    }, this._saveProductsRequest.bind(this));
  }

  /**Using for removing cart item in UI cart.component*/
  public removeCartLine(clientId: string): Promise<ICart> {
    const line = this.cart.lines.find((l) => l.clientId === clientId);

    this.cart = {
      ...this.cart,
      lines: this.cart.lines.filter((cartLine) => cartLine.clientId !== clientId)
    };

    this.trackRemoveFromCart([line]);

    if (this.cart.lines.length === 0) {
      return this.resetCart();
    }

    const _removeItem = () => {
      const updateCart$ = this._updateCountsForGroupProductIfNecessary(line);
      const removeLine$ = this._removeOrderLineAPI(clientId);

      return Promise
        .all([updateCart$, removeLine$])
        .then(([updateCart, cart]) => {
          this._notifyCartLinesRemovalSynced();
          return cart;
        });
    };

    this._removeProductFromStore(line && line.clientId || null);

    /**Chained after the queued removal rather than inside it: the queue serialises on a single
     * promise, so enqueueing from within a running task would wait on the task itself.*/
    return this
      .putInQueue({ queueId: CART_QUEUE_EVENTS.REMOVE_PRODUCT + clientId, payload: [clientId] }, _removeItem.bind(this))
      .then(() => this.dropOrphanedReturnPackagingLine());
  }

  /**Using for removing whole productType, for example when delete PIECES_GROUP product type from
   * product overview it will delete all items for this product from cart*/
  public removeProductType(orderLine: ICakeCartLine): Promise<ICart> {
    const removingLines = [];
    // keep previous source
    const { sourceType } = this.cart;

    this.cart.lines.forEach((line: ICartLine) => {
      if (
        line.type === CART_LINE_TYPES.PRODUCT &&
        // applyOnce lines dont have 'product' property
        line.product &&
        (orderLine.parentType && orderLine.orderLineBuilderId
          ? line.orderLineBuilderId === orderLine.orderLineBuilderId
          : line.product.type.id === orderLine.product.type.id)
      ) {
        removingLines.push(line);
      }
    });

    const removingClientIds = new Set(removingLines.map(({ clientId }) => clientId));

    this.cart = {
      ...this.cart,
      lines: this.cart.lines.filter(({ clientId }) => !removingClientIds.has(clientId))
    };

    removingLines.forEach(({ clientId }) => this._removeProductFromStore(clientId));

    this.trackRemoveFromCart(removingLines);

    if (this.cart.lines.length === 0) {
      return this.resetCart();
    }

    const _removeProductType = () => {
      const removeRequestsQueue = removingLines.map(line => this._removeOrderLineAPI(line.clientId));

      return Promise.all(removeRequestsQueue).then((carts) => {
        this._notifyCartLinesRemovalSynced();
        return carts[carts.length - 1];
      });
    };

    return this.putInQueue({
      queueId: CART_QUEUE_EVENTS.REMOVE_PRODUCT_TYPE,
      payload: [orderLine]
    }, _removeProductType.bind(this))
      .then(() => this.dropOrphanedReturnPackagingLine());
  }

  public removeAllCateringMenuLines(parentType: number): Promise<ICart> {
    const removingLines = this.cart.lines.filter((line: ICartLine) =>
      line.type === CART_LINE_TYPES.PRODUCT && line.parentType === parentType && !!line.clientId
    );

    if (!removingLines.length) {
      return Promise.resolve(this.cart);
    }

    const removingClientIds = new Set(removingLines.map(({ clientId }) => clientId));
    this.cart = {
      ...this.cart,
      lines: this.cart.lines.filter(({ clientId }) => !removingClientIds.has(clientId))
    };

    removingLines.forEach(({ clientId }) => this._removeProductFromStore(clientId));
    this.trackRemoveFromCart(removingLines);

    const removeRequestsQueue = removingLines.map(({ clientId }) => this._removeOrderLineAPI(clientId));
    const removeCateringMenu = () => Promise.all(removeRequestsQueue).then((carts) => {
      this._notifyCartLinesRemovalSynced();
      return carts[carts.length - 1];
    });

    return this.putInQueue({
      queueId: CART_QUEUE_EVENTS.REMOVE_CATERING_MENU + parentType,
      payload: [parentType]
    }, removeCateringMenu);
  }

  public resetCart(): Promise<ICart> {
    /**This condition means that the user directly clicked the "Clear cart" button on the cart*/
    if (this.cart && this.cart.lines.length > 0) {
      this.trackRemoveFromCart(this.cart.lines);
    }

    const _resetCart = () => {
      return firstValueFrom(this.deleteCartAPI())
        .then(() => this.fetchCart());
    };

    this._store.dispatch(new ResetCart());

    this.resetQueue();
    return this.putInQueue({ queueId: CART_QUEUE_EVENTS.RESET_CART, payload: [null] }, _resetCart.bind(this), 0);
  }

  public getDeliveryInfo(): Promise<ICartDeliveryInfo> {
    return this.putInQueue({
      queueId: CART_QUEUE_EVENTS.GET_CART_DELIVERY_INFO,
      payload: [null]
    }, this._getDeliveryInfo.bind(this));
  }

  public getDailyStockSoldOutProductTypes(): Promise<ICartDailyStockSoldOutProductType[]> {
    return firstValueFrom(this._api.get('api.cart', 'daily_stock_sold_out'));
  }

  public savePickup(deliveryInfo: any, options?: { dispatchCart?: boolean, priceReducePercent?: number }) {
    const model = {
      type: CART_DELIVERY_TYPES.PICKUP,
      outlet: deliveryInfo.outletId,

      deliveryDate: moment(deliveryInfo.date).format('YYYY-MM-DD'),
      deliveryTime: deliveryInfo.timeRange,
      externalDeliveryFee: 0
    };

    return this.saveDeliveryOrder(model, options);
  }

  public saveGiftDelivery(delivery: IOrderDelivery) {
    const model = pick(delivery, [
      'type', 'deliveryType', 'deliveryPhone', 'deliveryUserName', 'deliveryEmail', 'messageToRecipient', 'sendEmailDate', 'sendEmailTime'
    ]);

    return this.saveDeliveryOrder(model);
  }

  public saveDeliveryOrder(deliveryInfo: any,
                           options?: { dispatchCart?: boolean, priceReducePercent?: number }): Promise<any> {
    const defaultOptions = { dispatchCart: true, priceReducePercent: 0 };
    options = options ? { ...defaultOptions, ...options } : defaultOptions;
    const _saveDelivery = async () => {
      const orderId = deliveryInfo.id || this.cart.delivery && this.cart.delivery.orderId;
      const method = orderId ? 'put' : 'post';
      const multiOrder = await firstValueFrom(this._api[method]('api.cart', 'cart_delivery', null, deliveryInfo));

      return this.cart = this.initFromCartPayload(multiOrder, options);
    };

    return this.putInQueue({
      queueId: CART_QUEUE_EVENTS.SAVE_DELIVERY,
      payload: [deliveryInfo]
    }, _saveDelivery.bind(this));
  }

  public deleteDeliveryOrder() {
    const _deleteDeliveryOrder = async () => {
      const multiOrder = await firstValueFrom(this._api.remove('api.cart', 'cart_delivery'));
      return this.cart = this.initFromCartPayload(multiOrder, { dispatchCart: true });
    };

    return this.putInQueue({
      queueId: CART_QUEUE_EVENTS.DELETE_DELIVERY_ORDER,
      payload: [null]
    }, _deleteDeliveryOrder.bind(this));
  }

  /**`price` comes from the calendar timeslot the customer picked — the client is the only side that
   * knows which one it was. Self-returns are free, so it falls back to 0.*/
  public async saveReturnPackagingLine(details: IReturnPackagingDetails,
                                       price = 0,
                                       options?: IDispatchCartOptions): Promise<ICart> {
    const existing = this.cart?.returnPackaging;
    const existingId = existing?.id;

    const orderLine = {
      /**Left out when creating — the server keys a new line off client_id instead.*/
      ...(existingId && { id: existingId }),
      clientId: existing?.clientId || cryptoHelper.cartClientId(),
      type: CART_LINE_TYPES.RETURN_PACKAGING,
      bakery: this.cart?.bakery?.id,
      quantity: 1,
      price: price || 0,
      returnPackaging: details,
    };

    const multiOrder = await firstValueFrom(this._api
      .post('api.cart', 'order_lines', null, [orderLine]));

    return this.cart = this.initFromCartPayload(multiOrder, { dispatchCart: true, ...options });
  }

  public removeReturnPackagingLine(): Promise<ICart> {
    const existing = this.cart && this.cart.returnPackaging;

    if (!existing || !existing.clientId) {
      return Promise.resolve(this.cart);
    }

    const _removeReturnPackaging = async () => {
      await this._removeOrderLineAPI(existing.clientId);
      return this.fetchCart();
    };

    /**Shares the save queue id so a removal cannot overtake a save of the same line.*/
    return this.putInQueue({
      queueId: CART_QUEUE_EVENTS.SAVE_RETURN_PACKAGING,
      payload: [existing.clientId]
    }, _removeReturnPackaging.bind(this));
  }

  /**Removing the last product that needs returnable packaging leaves the return line behind: it
   * hangs off the cart, not off the product. The cart is editable from every step of the funnel, so
   * no step can own this — it belongs next to the removal itself. No-ops otherwise.*/
  public dropOrphanedReturnPackagingLine(): Promise<ICart> {
    if (!isReturnLineOrphaned(this.cart)) {
      return Promise.resolve(this.cart);
    }

    return this.removeReturnPackagingLine();
  }

  /**A return is scheduled against a particular delivery, so a delivery change can invalidate it.
   * No-ops when the cart carries no return line or the saved one still fits.*/
  public dropOutdatedReturnPackagingLine(): Promise<ICart> {
    const line = this.cart?.returnPackaging;

    if (!line || !isReturnLineOutdated(line, this.cart.delivery, getReturnSettings(this.cart))) {
      return Promise.resolve(this.cart);
    }

    return this.removeReturnPackagingLine();
  }

  public updatePrices(cart: ICart) {
    return cartHelper.getCartStateWithUpdatedPrices(cart);
  }

  public getMinQuantityForLine(line: ICakeCartLine) {
    return cartHelper.getMinQuantityForOrderLine(line, this.cart.lines);
  }

  public initFromCartPayload(multiOrder, options?: IDispatchCartOptions): ICart {
    const defaultOptions: IDispatchCartOptions = { dispatchCart: false, priceReducePercent: 0 };
    const {
      dispatchCart,
      priceReducePercent,
      deliveryPrice,
      customDeliveryPriceEnabled
    } = options ? { ...defaultOptions, ...options } : defaultOptions;

    const emptyBakeryOrder = {
      bakery: null,
      deliveryOrder: null,
      orderLines: [],
      price: null,
      priceWithoutVat: null,
      orderRequest: null,
    };
    const bakeryOrder = multiOrder.bakeryOrders[0] || emptyBakeryOrder;
    const bakeryId = bakeryOrder.bakery ? bakeryOrder.bakery.id : null;

    const bakery = bakeryOrder.bakery;
    const lines = cartHelper.mapBakeryOrderOrderLinesToCartLines(bakeryOrder.orderLines, { bakeryId, priceReducePercent });
    const delivery = cartHelper.mapDeliveryOrderFromCartResponse(bakeryOrder.deliveryOrder, bakery, {
      deliveryPrice,
      customDeliveryPriceEnabled
    });
    let appliedDiscount = cartHelper.getDiscountFromOrderLines(bakeryOrder.orderLines)
      || cartHelper.getDiscountFromOrderLines(multiOrder.orderLines);

    if (!appliedDiscount && multiOrder.discount) {
      appliedDiscount = {
        type: DISCOUNT_TYPES.USER_DISCOUNT,
        value: multiOrder.discount
      };
    }

    let cart: ICart = {
      id: multiOrder.id,
      lines,
      delivery,
      bakery,
      appliedDiscount,
      deliveryOrder: bakeryOrder.deliveryOrder,
      invoiceFee: this._actualInvoiceFee || 0,
      key: multiOrder.key,
      sourceType: multiOrder.sourceType,
      price: bakeryOrder.price,
      priceWithoutVat: bakeryOrder.priceWithoutVat,
      createdDate: multiOrder.createdDate,
      modifiedDate: multiOrder.modifiedDate,
      orderRequest: multiOrder.orderRequest,
      company: multiOrder.company,
      uiDeliveryType: cartHelper.defineDeliveryType(delivery),
      returnPackaging: cartHelper.extractReturnPackagingLine(bakeryOrder.orderLines),
    };

    extendWithPrice(cart, sumPrices(bakeryOrder, bakeryOrder.deliveryOrder));
    cart = this.updatePrices(cart);

    if (dispatchCart) {
      this._store.dispatch(new LoadCartSuccess(cart));
    }

    this._persistsKey(cart.key);
    return cart;
  }

  public getKey(): string {
    const webStorage = this._storage.getMostPersistentStorage();
    return webStorage.get(CART_KEY_STORAGE_KEY);
  }

  public cleanKey(): void {
    const webStorage = this._storage.getMostPersistentStorage();
    webStorage.remove(CART_KEY_STORAGE_KEY);
  }

  public setDeliveryInfoSettings(settings: IAdditionalDeliveryInfo) {
    this._store.dispatch(new DeliveryInfoUpdate(settings));
  }

  public deleteCartAPI(): Observable<any> {
    return this._api.remove('api.cart', 'cart');
  }

  public normalizeCartLineBeforeSaving(cartLine: ICakeCartLine): ICakeCartLine {
    if (!cartLine.clientId) {
      cartLine.clientId = cryptoHelper.cartClientId();
    }

    if (!cartLine.orderLineBuilderId && cartHelper.canHaveOrderLineBuilderId(cartLine)) {
      cartLine.orderLineBuilderId = cryptoHelper.cartClientId();
    }

    return cartLine;
  }

  public putCartLineToStore(cartLine: ICakeCartLine) {
    this._store.dispatch(new UpdateLine(cartLine));
  }

  public putInQueueProductSaving(cartLine: ICakeCartLine, options?: IDispatchCartOptions): Promise<ICart> {
    return this.putInQueue({
      queueId: CART_QUEUE_EVENTS.SAVE_PRODUCT + cartLine.clientId,
      payload: [[cartLine], options]
    }, this._saveProductsRequest.bind(this));
  }

  public _getDeliveryInfo(): Promise<ICartDeliveryInfo> {
    return firstValueFrom(this._api.get('api.cart', 'delivery_info'));
  }

  private trackRemoveFromCart(lines: ICartLine[]) {
    try {
      this.trackers.forEach((tracker) => {
        tracker.trackRemoveFromCart(this.cart, lines);
      });
    } catch (e) {
      console.error('CartV2Service trackRemoveFromCart error: ', e);
    }
  }

  private _removeProductFromStore(clientId: string) {
    this._store.dispatch(new RemoveLine(clientId));
  }

  private _updateCountsForGroupProductIfNecessary(line: ICakeCartLine): Promise<ICart> {
    if (!line.isCustom && line.size.details.pieces) {
      const groupOfSimilarLines = cartHelper.getGroupOfLines(line, this.cart.lines);
      if (groupOfSimilarLines.length > 1) {
        const linesMetaWithIncorrectCount = groupOfSimilarLines
          .map((l) => ({
            line: l,
            minQuantity: this.getMinQuantityForLine(l)
          }))
          .filter((meta) => meta.line.clientId !== line.clientId
            && meta.minQuantity > meta.line.count);

        if (linesMetaWithIncorrectCount.length > 0) {
          const lineToFixCount = linesMetaWithIncorrectCount[0];
          lineToFixCount.line.count = lineToFixCount.minQuantity;
          return this.saveProducts([lineToFixCount.line]);
        }
      }
    }
    return Promise.resolve(this.cart);
  }

  private _persistsKey(key: string): void {
    const webStorage = this._storage.getMostPersistentStorage();
    webStorage.save(CART_KEY_STORAGE_KEY, key);
  }

  private _saveProductsRequest(cartLines: ICakeCartLine[], options?: IDispatchCartOptions): Promise<ICart> {
    const orderLinesForServer = cartLines.map(l => cartHelper.transformToServerCartLine(l));

    return firstValueFrom(this._api.post('api.cart', 'order_lines', null, orderLinesForServer))
      .then((cart) => {
        this.cart = this.initFromCartPayload(cart, options);
        this.setDeliveryInfoSettings({
          newDeliveryPrice: this.cart.delivery && this.cart.delivery.price,
        });
        return this.cart;
      })
      .catch((ex) => {
        // Raven.captureException(ex, 'Adding/Updating cart item error');
        return Promise.reject(ex);
      });
  }

  private _removeOrderLineAPI(clientId: string) {
    return firstValueFrom(this._api.remove('api.cart', 'order_lines_one', { clientId }));
  }

  private _notifyCartLinesRemovalSynced() {
    this.cartLinesRemovalSynced$.next();
  }
}
